<template>
    <tailwind-layout title="Rental Payments">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between"> <!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Rent Payments</h1>
                    <h2 class="text-xs leading-tight">This is where you manage properties for routine, passive rent payments.</h2>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link :href="route('rented-properties.create')" class="primaryButton" data-demo-target="add-property-btn">
                        Add Property
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-start md:items-center">
                    <div class="flex flex-col">
                        <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Rent Payments</h1>
                        <h2 class="text-xs">This is where you manage properties for routine, passive rent payments.</h2>
                    </div>
                    <inertia-link class="primaryButton" :href="route('rented-properties.create')" data-demo-target="add-property-btn">
                        Add Property
                    </inertia-link>
                </div>
            </div>

        </template>
        
        <!-- Plaid Onboarding Warning Banner -->
        <div v-if="$page.props.user.role === 'agent' && transfer_diligence_status !== 'approved' && subscription_data.has_access" class="rounded-md bg-yellow-50 p-4 mb-6">
            <div class="flex">
                <div class="flex-shrink-0">
                    <font-awesome-icon :icon="faExclamationTriangle" class="text-yellow-400" />
                </div>
                <div class="ml-3 flex-1">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <p class="text-sm text-yellow-700">
                            <strong>Payment Processing Setup Required:</strong> 
                            To complete your rent payments setup, please complete identity verification and bank account setup through Plaid.
                        </p>
                        <div class="mt-3 sm:mt-0 sm:ml-4">
                            <inertia-link :href="route('payment-methods.index')" class="whitespace-nowrap font-bold text-sm text-yellow-700 hover:text-yellow-600">
                                Complete Setup
                                <span aria-hidden="true"> &rarr;</span>
                            </inertia-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0" data-demo-target="search-filters">
                    <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1">
                        <select v-model="form.paginate"
                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                aria-label="Number of items per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search for Properties..."
                               class="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                        <div class="flex items-center mt-2" @click="form.trashed = !form.trashed">
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Properties</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                    <div class="w-full col-span-4 sm:w-4/12 order-3 sm:order-3 mt-1 text-right" data-demo-target="transaction-buttons">
                        <inertia-link :href="route('rented-properties.transactions.user')" class="whiteButton mr-2">
                            <font-awesome-icon icon="fa-money-check" class="pr-2" /> My Transactions
                        </inertia-link>
                        <inertia-link :href="route('sweeps.index')" class="whiteButton">
                            <font-awesome-icon icon="fa-money-check" class="pr-2" /> My Payouts
                        </inertia-link>
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by"/>
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction"/>
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table" data-demo-target="rental-properties-table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Address
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">
                                            Tenant
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">
                                            Lease Term
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-right">
                                            Monthly Rent
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody v-for="(property, index) in properties.data" :key="index"
                                        :class="[
                                            index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-white hover:bg-yellow-50',
                                            past_due_properties.properties[property.id] && past_due_properties.properties[property.id].is_past_due ? '!bg-red-100 hover:!bg-yellow-50' : '',
                                            property.rental_property_deleted_at ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''
                                        ]"
                                    >
                                    <tr v-if="!property.rental_property_deleted_at">
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <div class="flex justify-between">
                                                <inertia-link
                                                    title="Edit Property"
                                                    :href="route('rented-properties.edit', property.id)"
                                                    class="text-indigo-600">
                                                    {{ property.full_address }}
                                                </inertia-link>
                                                <div v-if="past_due_properties.properties[property.id] && past_due_properties.properties[property.id].is_past_due">
                                                    <span class="bg-red-700 px-4 py-1 rounded-lg text-white text-sm">OVERDUE</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 text-center">
                                            <span v-if="property.tenant?.name">
                                                {{ property.tenant.name }}
                                            </span>
                                            <span v-else-if="property.invitation">
                                                {{ property.invitation.email }}
                                                <button @click="showResendInvitationPopup(property.rent_info?.id)" type="button" class="ml-2 text-sm text-indigo-600 hover:text-indigo-500 font-medium underline">Resend Invitation</button>
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <div v-if="property.rent_info">
                                                {{ property.rent_info.lease_start_date+' - '+property.rent_info.lease_end }}
                                                <span :class="(property.rent_info.status === 'Active') ? 'inline-flex items-center rounded-md bg-green-50 ml-2 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20' : 'inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10'
                                                    ">
                                                    {{ property.rent_info.status }}
                                                </span>
                                            </div>
                                        </td>
                                        <td v-if="property.rental_property_deleted_at" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                           <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-800 text-white">
                                                archived
                                            </span>
                                        </td>
                                        <td v-else class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 text-right">
                                            ${{ property.rent }}
                                        </td>
                                    </tr>
                                    <tr v-else class="bg-red-100 hover:bg-red-200 pt-4 pb-4">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {{ property.full_address }}
                                        </td>
                                        <td></td>

                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                               <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-800 text-white">
                                                    archived
                                                </span>
                                        </td>
                                        <td colspan="4" class="text-right pb-2">
                                            <span class="isolate inline-flex rounded-md shadow-sm mr-3">
                                                <button
                                                    title="Archive Property"
                                                    @click="restoreProperty(property)"
                                                    class="relative -ml-px inline-flex items-center rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Restore Property
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="!property.rental_property_deleted_at">
                                        <td colspan="4" class="text-right pb-2">
                                            <span class="isolate inline-flex rounded-md shadow-sm mr-3">
                                                <inertia-link
                                                    title="Monthly Rental Ledger"
                                                    :href="(property.lease_status) ? route('rented-properties.payments', property.rent_info.id) : '#'"
                                                    :class="[
                                                    (!property.lease_status) ? 'opacity-50 cursor-not-allowed' : '',
                                                    'relative inline-flex items-center rounded-l-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10'
                                                    ]
                                                    ">
                                                    Monthly Rental Ledger
                                                </inertia-link>

                                                <inertia-link
                                                    v-if="!property.lease_status"
                                                    title="Add Lease Information"
                                                    :href="route('rented-properties.create-lease-information', property.id)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Add Lease Information
                                                </inertia-link>
                                                <inertia-link
                                                    v-else
                                                    title="Edit Lease Information"
                                                    :href="route('rented-properties.edit-lease', property.rent_info.id)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                     Edit Lease Information
                                                </inertia-link>

                                                <inertia-link
                                                    title="Property Transactions"
                                                    :href="route('rented-properties.property-transactions', property.id)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Transactions
                                                </inertia-link>

                                                <inertia-link
                                                    title="Property Lease History"
                                                    :href="route('rented-properties.lease-history', property.id)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Lease History
                                                </inertia-link>

                                                <inertia-link
                                                    title="Edit Property"
                                                    :href="route('rented-properties.edit', property.id)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Edit Property
                                                </inertia-link>

                                                <button
                                                    title="Archive Property"
                                                    @click="archiveProperty(property)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Archive Property
                                                </button>
                                                <button
                                                    v-if="property.rent_info && property.rent_info.status === 'Active'"
                                                    title="Stop Accepting Rent Payments"
                                                    @click="openStopRentPaymentsModal(property)"
                                                    class="relative -ml-px inline-flex items-center rounded-r-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-100 focus:z-10">
                                                    Stop Accepting Rent Payments
                                                    <tool-tip
                                                        class="ml-2"
                                                        title="Stop Accepting Rent Payments"
                                                        description="If you no longer wish to receive rental payments or want to block your tenant from paying additional payments, click this button.">
                                                    </tool-tip>
                                                </button>
                                                <button
                                                    v-if="property.rent_info && property.rent_info.status === 'Rent Payments Stopped'"
                                                    title="Resume Rent Payments"
                                                    @click="openResumeRentPaymentsModal(property)"
                                                    class="relative -ml-px inline-flex items-center rounded-r-md bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 ring-1 ring-inset ring-green-200 hover:bg-green-100 focus:z-10">
                                                    Resume Rent Payments
                                                </button>
                                            </span>
                                            <div class="mt-1 mr-3">
                                            </div>
                                        </td>
                                    </tr>

                                    <tr v-show="!properties.data.length">
                                        <td colspan="4"
                                            class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No Properties were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(property, index) in properties.data" :key="index" :class="[property.rental_property_deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                        <inertia-link :href="route('properties.show', property.id)">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">{{ property.full_address }}</h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Monthly Rent:</span> {{ property.rent }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Tenant:</span> {{ property.tenant ? property.tenant.name : '' }}</h4>
                                            <h4 class="mt-1 text-sm text-gray-500"><span class="text-gray-900">Lease Term: </span> 
                                                <span v-if="property.rent_info">
                                                 {{ property.rent_info.lease_start_date+' - '+property.rent_info.lease_end }}
                                                    <span :class="(property.rent_info.status === 'Active') ? 'inline-flex items-center rounded-md bg-green-50 ml-2 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20' : 'inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10'
                                                        ">
                                                        {{ property.rent_info.status }}
                                                    </span>
                                                </span>
                                            </h4>
                                        </div>
                                        </inertia-link>
                                        <div>
                                            <div :class="[property.rental_property_deleted_at ? '' : 'divide-gray-200','-mt-px flex divide-x divide-y']">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link
                                                        title="Monthly Rental Ledger"
                                                        :href="(property.lease_status) ? route('rented-properties.payments', property.rent_info.id) : '#'"
                                                        :class="[
                                                        (!property.lease_status) ? 'opacity-50 cursor-not-allowed' : '',
                                                        ]"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900"
                                                        >
                                                        <font-awesome-icon :icon="faMoneyCheckDollar" />
                                                        Monthly Rental Ledger
                                                    </inertia-link>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <inertia-link
                                                        v-if="!property.lease_status"
                                                        title="Add Lease Information"
                                                        :href="route('rented-properties.create-lease-information', property.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900"
                                                        >
                                                        <font-awesome-icon icon="fa-money-check" />
                                                        Add Lease Information
                                                    </inertia-link>
                                                    <inertia-link
                                                        v-else
                                                        title="Edit Lease Information"
                                                        :href="route('rented-properties.edit-lease', property.rent_info.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900"
                                                        >
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit Lease Information
                                                    </inertia-link>
                                                </div>
                                            </div>
                                            <div :class="[property.rental_property_deleted_at ? '' : 'divide-gray-200','-mt-px flex divide-x divide-y']">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link
                                                        title="Property Transactions"
                                                        :href="route('rented-properties.property-transactions', property.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 border py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-money-check" />
                                                        Transactions
                                                    </inertia-link>
                                                </div>
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link
                                                        title="Property Lease History"
                                                        :href="route('rented-properties.lease-history', property.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon :icon="faClockRotateLeft" />
                                                        Lease History
                                                    </inertia-link>
                                                </div>
                                            </div>
                                            <div :class="[property.rental_property_deleted_at ? '' : 'divide-gray-200','-mt-px flex divide-x divide-y']">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link
                                                        title="Edit Property"
                                                        :href="route('rented-properties.edit', property.id)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 rounded-bl-lg border py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit Property
                                                    </inertia-link>
                                                </div>
                                                <div class="flex w-0 flex-1">
                                                    <button
                                                        title="Archive Property"
                                                        @click="archiveProperty(property)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 pl-4 gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-trash" />
                                                        Archive Property
                                                    </button>
                                                </div>
                                            </div>
                                            <div class="-mt-px flex divide-x divide-y" v-if="property.rent_info">
                                                <div class="flex w-0 flex-1" v-if="property.rent_info.status === 'Active'">
                                                    <button
                                                        title="Stop Accepting Rent Payments"
                                                        @click="openStopRentPaymentsModal(property)"
                                                        class="hover:bg-red-100 relative inline-flex w-0 flex-1 pl-4 gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-red-700">
                                                        <i class="fa fa-ban"></i>
                                                        Stop Accepting Rent Payments
                                                        <tool-tip
                                                            class="ml-2"
                                                            title="Stop Accepting Rent Payments"
                                                            description="Block tenants from making additional rent payments.">
                                                        </tool-tip>
                                                    </button>
                                                </div>
                                                <div class="flex w-0 flex-1" v-else-if="property.rent_info.status === 'Rent Payments Stopped'">
                                                    <button
                                                        title="Resume Rent Payments"
                                                        @click="openResumeRentPaymentsModal(property)"
                                                        class="hover:bg-green-100 relative inline-flex w-0 flex-1 pl-4 gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-green-700">
                                                        <i class="fa fa-rotate-right"></i>
                                                        Resume Rent Payments
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="properties.links" :results="properties" :type="'properties'"/>
        </div>
    </tailwind-layout>
    <div v-if="showArchiveModal">
        <TransitionRoot as="template" :show="showArchiveModal">
            <Dialog as="div" class="relative z-10" @close="showArchiveModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                        <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle v-if="!selectedProperty.lease_status" as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to archive this property?</DialogTitle>
                                        <DialogTitle v-else as="h3" class="text-base font-semibold leading-6 text-gray-900">You currently have an active lease for this property. Would you like to mark the lease as completed and archive the property?</DialogTitle>
                                        <div class="mt-2">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveModal = false">Cancel</button>
                                    <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="destroy()">Confirm</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>

    <div v-if="showRestoreModal">
        <TransitionRoot as="template" :show="showRestoreModal">
            <Dialog as="div" class="relative z-10" @close="showRestoreModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                        <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this property?</DialogTitle>
                                        <div class="mt-2">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600" @click="showRestoreModal = false">Close</button>
                                    <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="restore()">Confirm</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>

    <stop-rent-payments-modal
        :show="showStopRentPaymentsModal"
        :property-address="selectedRentProperty?.full_address"
        @close="showStopRentPaymentsModal = false"
        @confirm="stopRentPayments"
    />

    <resume-rent-payments-modal
        :show="showResumeRentPaymentsModal"
        :property-address="selectedRentProperty?.full_address"
        @close="showResumeRentPaymentsModal = false"
        @confirm="resumeRentPayments"
    />

    <resend-invitation :show="showResendInvitationModal" @close="showResendInvitationModal = false" @confirm="resendInvitation"></resend-invitation>
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'rented-properties.index'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    faArrowsRotate,
    faBoxArchive,
    faClockRotateLeft,
    faExclamationTriangle,
    faMoneyCheckDollar
} from '@fortawesome/pro-solid-svg-icons';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";
import ResendInvitation from '@/Shared/ResendInvitation.vue'
import ToolTip from '@/Shared/ToolTip.vue'
import StopRentPaymentsModal from '@/Pages/RentedProperties/Components/StopRentPaymentsModal.vue'
import ResumeRentPaymentsModal from '@/Pages/RentedProperties/Components/ResumeRentPaymentsModal.vue'

export default {
    components: {
        DialogTitle,
        DialogPanel,
        TransitionChild,
        Dialog,
        TransitionRoot,
        FontAwesomeIcon,
        Icon,
        Pagination,
        TailwindLayout,
        ResendInvitation,
        DemoWidget,
        ToolTip,
        StopRentPaymentsModal,
        ResumeRentPaymentsModal,
    },
    props: {
        filters: Object,
        properties: Object,
        past_due_properties: Object,
        transfer_diligence_status: String,
        subscription_data: Object,
    },
    data() {
        return {
            faArrowsRotate,
            selectedProperty: null,
            selectedRentProperty: null,
            showArchiveModal: false,
            showRestoreModal: false,
            showStopRentPaymentsModal: false,
            showResumeRentPaymentsModal: false,
            demoSteps: getDemoSteps('rented-properties.index'),
            form: {
                search: this.filters.search,
                paginate: (this.filters.paginate) ? this.filters.paginate : 100,
                sort_by: this.filters.sort_by,
                sort_direction_icon: (this.filters.sort_direction === 'desc') ? 'sort-up' : 'sort-down'
            },
            showResendInvitationModal: false,
            selectedLeaseTermId: null,
            selectedRentLeaseTermId: null,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('rented-properties.index'), pickBy(this.form), {preserveState: true})
            }, 150),
        },
    },
    methods: {
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc') ? 'asc' : 'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc') ? 'sort-up' : 'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        archiveProperty(property) {
            this.selectedProperty = property;
            this.showArchiveModal = true;
        },
        destroy() {
            this.$inertia.delete(this.route('rented-properties.destroy', this.selectedProperty.id), {
                onFinish: () => {
                    this.showArchiveModal = false;
                }
            })
        },
        restoreProperty(property) {
            this.selectedProperty = property;
            this.showRestoreModal = true;
        },
        restore() {
            this.$inertia.put(this.route('rented-properties.restore', this.selectedProperty.id), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },

        showResendInvitationPopup(leaseTermId) {
            this.selectedLeaseTermId = leaseTermId
            this.showResendInvitationModal = true
        },

        resendInvitation() {
            this.$inertia.post(this.route('rent-payments.resend-invitation', this.selectedLeaseTermId), {},{
                onFinish: () => {
                    this.showResendInvitationModal = false;
                }
            })
        },
        openStopRentPaymentsModal(property) {
            this.selectedRentProperty = property;
            this.selectedRentLeaseTermId = property.rent_info?.id;
            this.showStopRentPaymentsModal = true;
        },
        openResumeRentPaymentsModal(property) {
            this.selectedRentProperty = property;
            this.selectedRentLeaseTermId = property.rent_info?.id;
            this.showResumeRentPaymentsModal = true;
        },
        stopRentPayments() {
            if(!this.selectedRentLeaseTermId) {
                return;
            }
            this.$inertia.post(this.route('rented-properties.stop-rent-payments', this.selectedRentLeaseTermId), {}, {
                onFinish: () => {
                    this.showStopRentPaymentsModal = false;
                    this.selectedRentLeaseTermId = null;
                    this.selectedRentProperty = null;
                }
            });
        },
        resumeRentPayments() {
            if(!this.selectedRentLeaseTermId) {
                return;
            }
            this.$inertia.post(this.route('rented-properties.resume-rent-payments', this.selectedRentLeaseTermId), {}, {
                onFinish: () => {
                    this.showResumeRentPaymentsModal = false;
                    this.selectedRentLeaseTermId = null;
                    this.selectedRentProperty = null;
                }
            });
        },
    },
}
</script>
